import React, { useEffect, useState } from "react";
import { db } from "../firebase-config";
import { collection, query, where, getDocs, orderBy, updateDoc, doc, arrayUnion } from "firebase/firestore";
import "./AdminDashboard.css";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { useNavigate } from "react-router-dom";
dayjs.locale("fr");

const statusColors = {
  "Validé": "#43a047",
  "Refusé": "#e53935",
  "En attente": "#ff9800"
};

function formatDateFR(dateStr) {
  if (!dateStr) return '';
  const [yyyy, mm, dd] = dateStr.split('-');
  return `${dd}-${mm}-${yyyy}`;
}

// Fonction utilitaire pour parser la date de l'historique
function parseHistoryDate(date) {
  if (!date) return null;
  if (typeof date === 'string') return new Date(date);
  if (date instanceof Date) return date;
  if (date.seconds !== undefined) return new Date(date.seconds * 1000);
  if (typeof date.toDate === 'function') return date.toDate();
  return null;
}

// Fonction utilitaire pour savoir si une absence est une demi-journée sur un jour donné
function isHalfDay(abs, day) {
  const normalize = v => (v || '').toLowerCase().replace(/[-_ ]/g, '');
  const start = dayjs(abs.startDate);
  const end = dayjs(abs.endDate);
  const d = dayjs(day);
  const startPart = normalize(abs.startPart);
  const endPart = normalize(abs.endPart);
  // matin = matin, am = après-midi, apresmidi, aprèsmidi, etc.
  const isMatin = v => v === 'matin';
  const isAM = v => v === 'am' || v === 'apresmidi' || v === 'aprèsmidi' || v === 'après-midi' || v === 'apres-midi';
  if (start.isSame(end, 'day') && start.isSame(d, 'day')) {
    if (isMatin(startPart) && isMatin(endPart)) return 'left';
    if (isAM(startPart) && isAM(endPart)) return 'right';
  }
  if (start.isSame(d, 'day') && isAM(startPart)) return 'right';
  if (end.isSame(d, 'day') && isMatin(endPart)) return 'left';
  return null;
}

function AdminAbsenceManagement() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [adminComment, setAdminComment] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [absences, setAbsences] = useState([]);
  const [absLoading, setAbsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Calendrier absences
  const [calendarMonth, setCalendarMonth] = useState(dayjs().startOf('month'));
  const [calendarAbsences, setCalendarAbsences] = useState([]);
  const navigate = useNavigate();

  // --- Nouvelle section : Liste mensuelle des absences ---
  const [listMonth, setListMonth] = useState(dayjs().startOf('month'));
  const [listAbsences, setListAbsences] = useState([]);

  useEffect(() => {
    const fetchPending = async () => {
      setLoading(true);
      const q = query(
        collection(db, "absences"),
        where("status", "==", "En attente"),
        orderBy("createdAt", "desc")
      );
      const snap = await getDocs(q);
      setPending(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
      
      // Marquer toutes les demandes en attente comme vues par l'admin
      const markAsViewed = async () => {
        const unviewedAbsences = snap.docs.filter(doc => !doc.data().vuParAdmin);
        for (const absenceDoc of unviewedAbsences) {
          try {
            await updateDoc(doc(db, "absences", absenceDoc.id), {
              vuParAdmin: true
            });
          } catch (error) {
            console.error("Erreur lors du marquage comme vue:", error);
          }
        }
      };
      markAsViewed();
    };
    fetchPending();
  }, []);

  useEffect(() => {
    const fetchAbsences = async () => {
      setAbsLoading(true);
      const q = query(
        collection(db, "absences"),
        orderBy("startDate", "asc")
      );
      const snap = await getDocs(q);
      setAbsences(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setAbsLoading(false);
    };
    fetchAbsences();
  }, []);

  useEffect(() => {
    const fetchCalendarAbsences = async () => {
      const q = query(
        collection(db, "absences"),
        orderBy("startDate", "asc")
      );
      const snap = await getDocs(q);
      setCalendarAbsences(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchCalendarAbsences();
  }, []);

  useEffect(() => {
    // On réutilise absences déjà chargées
    setListAbsences(absences);
  }, [absences]);

  // Navigation mois/année indépendante
  const handlePrevListMonth = () => setListMonth(listMonth.subtract(1, 'month'));
  const handleNextListMonth = () => setListMonth(listMonth.add(1, 'month'));

  // Filtrage des absences qui touchent le mois affiché
  const filteredListAbsences = listAbsences.filter(abs => {
    const start = dayjs(abs.startDate);
    const end = dayjs(abs.endDate);
    return (
      (start.isBefore(listMonth.endOf('month')) || start.isSame(listMonth.endOf('month'), 'day')) &&
      (end.isAfter(listMonth.startOf('month')) || end.isSame(listMonth.startOf('month'), 'day'))
    );
  });

  // Tri alphabétique par nom
  const sortedListAbsences = [...filteredListAbsences].sort((a, b) => {
    const nomA = (a.nom || '').toLowerCase();
    const nomB = (b.nom || '').toLowerCase();
    if (nomA < nomB) return -1;
    if (nomA > nomB) return 1;
    return 0;
  });

  // Rendu de la nouvelle section
  // Couleur de fond selon le statut
  const getRowColor = (status) => {
    if (status === 'Validé') return '#e8f5e9';
    if (status === 'Refusé') return '#ffebee';
    if (status === 'En attente') return '#fff8e1';
    return 'white';
  };
  const getBorderColor = (status) => {
    if (status === 'Validé') return '#43a047';
    if (status === 'Refusé') return '#e53935';
    if (status === 'En attente') return '#ff9800';
    return '#ccc';
  };

  // Ajout pour l'historique
  function getHistoryArray(abs) {
    if (!abs.history || !Array.isArray(abs.history)) {
      // Si pas d'historique, on initialise avec le statut actuel
      return [{
        date: abs.statusUpdatedAt || abs.createdAt || new Date(),
        statut: abs.status,
        commentaire: abs.adminComment || ""
      }];
    }
    // Tri du plus récent au plus ancien
    return [...abs.history].sort((a, b) => new Date(b.date) - new Date(a.date));
  }

  const handleAction = async (status) => {
    if (!selected) return;
    setActionLoading(true);
    try {
      const now = new Date();
      const newHistory = {
        date: now,
        statut: status,
        commentaire: adminComment || ""
      };
      await updateDoc(doc(db, "absences", selected.id), {
        status: status,
        adminComment: adminComment,
        statusUpdatedAt: now,
        history: arrayUnion(newHistory),
        vuParEmploye: false
      });
      setSelected(null);
      setAdminComment("");
      // Refresh list
      const q = query(
        collection(db, "absences"),
        where("status", "==", "En attente"),
        orderBy("createdAt", "desc")
      );
      const snap = await getDocs(q);
      setPending(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      // Refresh absences et calendrier
      const q2 = query(collection(db, "absences"), orderBy("startDate", "asc"));
      const snap2 = await getDocs(q2);
      setAbsences(snap2.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setCalendarAbsences(snap2.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } finally {
      setActionLoading(false);
    }
  };

  const pagedAbsences = absences.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const totalAbs = absences.length;

  // Générer la grille du mois
  const daysInMonth = calendarMonth.daysInMonth();
  const firstDayOfWeek = calendarMonth.startOf('month').day(); // 0=dimanche, 1=lundi
  const weeks = [];
  let day = 1 - (firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1); // Commencer lundi
  while (day <= daysInMonth) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      if (day > 0 && day <= daysInMonth) {
        week.push(day);
      } else {
        week.push(null);
      }
      day++;
    }
    weeks.push(week);
  }

  // Absences à afficher sur le mois courant
  const absencesByDay = {};
  calendarAbsences.forEach(abs => {
    const start = dayjs(abs.startDate);
    const end = dayjs(abs.endDate);
    for (let d = start; d.isBefore(end) || d.isSame(end, 'day'); d = d.add(1, 'day')) {
      if (d.month() === calendarMonth.month() && d.year() === calendarMonth.year()) {
        const key = d.date();
        if (!absencesByDay[key]) absencesByDay[key] = [];
        absencesByDay[key].push(abs);
      }
    }
  });

  return (
    <div className="admin-dashboard" style={{padding: 32, maxWidth: 1200, margin: '0 auto'}}>
      <button 
        onClick={() => navigate('/admin')}
        style={{marginBottom: 24, background: '#2196F3', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 600, fontSize: 16, cursor: 'pointer'}}
      >⏪ Retour</button>
      <h1 style={{marginBottom: 32}}>Gestion des Absences</h1>
      <section style={{marginBottom: 48}}>
        <h2 style={{fontSize: 22, marginBottom: 18}}>Gestion des demandes</h2>
        {loading ? (
          <div>Chargement...</div>
        ) : pending.length === 0 ? (
          <div>Aucune demande en attente.</div>
        ) : (
          <div style={{width: '100%', maxWidth: 600}}>
            {pending.map(req => (
              <div key={req.id} style={{
                background: '#f8f9fa',
                borderRadius: 12,
                padding: 16,
                marginBottom: 14,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                cursor: 'pointer',
                position: 'relative'
              }}
                onClick={() => { setSelected(req); setAdminComment(req.adminComment || ""); }}
              >
                <div style={{fontWeight: 600, fontSize: 16, marginBottom: 4}}>{req.type}</div>
                <div style={{fontSize: 14, color: '#444', marginBottom: 4}}>
                  <b>{req.employeeName || req.employeeEmail}</b>
                </div>
                <div style={{fontSize: 14, color: '#444', marginBottom: 6}}>
                  Du {formatDateFR(req.startDate)} ({req.startPart})<br />
                  au {formatDateFR(req.endDate)} ({req.endPart})
                </div>
                <span style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  background: statusColors[req.status] || '#bbb',
                  color: '#fff',
                  borderRadius: 12,
                  padding: '2px 12px',
                  fontSize: 13,
                  fontWeight: 600
                }}>{req.status}</span>
              </div>
            ))}
          </div>
        )}
        {/* Popup détail demande */}
        {selected && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.25)',
            zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 32,
              minWidth: 320,
              maxWidth: 400,
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              position: 'relative',
              display: 'flex', flexDirection: 'column', alignItems: 'center'
            }}>
              <button onClick={() => setSelected(null)}
                style={{position: 'absolute', top: 18, right: 18, background: 'none', border: 'none', fontSize: 28, cursor: 'pointer', color: '#333', width: 36, height: 36, lineHeight: 1, zIndex: 2}}
                aria-label="Fermer"
              >×</button>
              <h3 style={{marginBottom: 12}}>Demande d'absence - Détails</h3>
              {/* Historique des statuts */}
              <div style={{width: '100%', marginBottom: 12}}>
                {getHistoryArray(selected).map((h, idx) => (
                  <div key={idx} style={{fontSize: 13, color: '#555', marginBottom: 4, borderLeft: '4px solid #eee', paddingLeft: 8}}>
                    <b>{h.statut}</b> le {h.date && (parseHistoryDate(h.date)?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) || '-')}
                    {h.commentaire && <><br /><span style={{color:'#888'}}>Commentaire : {h.commentaire}</span></>}
                  </div>
                ))}
              </div>
              <div style={{fontWeight: 600, fontSize: 16, marginBottom: 4}}>{selected.type}</div>
              <div style={{fontSize: 14, color: '#444', marginBottom: 4}}><b>{selected.employeeName || selected.employeeEmail}</b></div>
              <div style={{fontSize: 14, color: '#444', marginBottom: 6}}>
                Du {formatDateFR(selected.startDate)} ({selected.startPart})<br />
                au {formatDateFR(selected.endDate)} ({selected.endPart})
              </div>
              <span style={{
                background: statusColors[selected.status] || '#bbb',
                color: '#fff',
                borderRadius: 12,
                padding: '2px 12px',
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 10
              }}>{selected.status}</span>
              <div style={{fontSize: 13, color: '#888', marginBottom: 8}}>
                Demande émise le {selected.createdAt && selected.createdAt.toDate ? formatDateFR(selected.createdAt.toDate().toISOString().slice(0,10)) : "-"}
              </div>
              <div style={{fontSize: 14, color: '#444', marginBottom: 10}}>
                <b>Commentaire salarié :</b><br />{selected.comment || <span style={{color:'#bbb'}}>Aucun</span>}
              </div>
              <div style={{fontSize: 14, color: '#444', marginBottom: 10, width: '100%'}}>
                <b>Commentaire employeur :</b><br />
                <textarea
                  value={adminComment}
                  onChange={e => setAdminComment(e.target.value)}
                  style={{width: '100%', minHeight: 50, borderRadius: 8, border: '1px solid #ccc', marginTop: 4, padding: 8}}
                  placeholder="Votre commentaire (optionnel)"
                />
              </div>
              <div style={{display: 'flex', gap: 16, marginTop: 12}}>
                <button
                  style={{background: '#43a047', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 600, fontSize: 16, cursor: 'pointer'}}
                  disabled={actionLoading}
                  onClick={() => handleAction("Validé")}
                >Accepter</button>
                <button
                  style={{background: '#e53935', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 600, fontSize: 16, cursor: 'pointer'}}
                  disabled={actionLoading}
                  onClick={() => handleAction("Refusé")}
                >Refuser</button>
              </div>
            </div>
          </div>
        )}
      </section>
      <section>
        <h2 style={{fontSize: 22, marginBottom: 18}}>Rappel des prochaines absences</h2>
        {absLoading ? (
          <div>Chargement...</div>
        ) : (
          <>
            <table style={{width: '100%', maxWidth: 900, margin: '0 auto', background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', fontSize: 15}}>
              <thead>
                <tr style={{background: '#f5f5f5'}}>
                  <th style={{padding: 8}}>Nom</th>
                  <th style={{padding: 8}}>Date début</th>
                  <th style={{padding: 8}}>Début</th>
                  <th style={{padding: 8}}>Date fin</th>
                  <th style={{padding: 8}}>Fin</th>
                  <th style={{padding: 8}}>Date demande</th>
                  <th style={{padding: 8}}>Statut</th>
                </tr>
              </thead>
              <tbody>
                {pagedAbsences.map(abs => (
                  <tr key={abs.id} style={{cursor: 'pointer'}} onClick={() => { setSelected(abs); setAdminComment(abs.adminComment || ""); }}>
                    <td style={{padding: 8}}>{abs.employeeName || abs.employeeEmail}</td>
                    <td style={{padding: 8}}>{formatDateFR(abs.startDate)}</td>
                    <td style={{padding: 8}}>{abs.startPart}</td>
                    <td style={{padding: 8}}>{formatDateFR(abs.endDate)}</td>
                    <td style={{padding: 8}}>{abs.endPart}</td>
                    <td style={{padding: 8}}>{abs.createdAt && abs.createdAt.toDate ? formatDateFR(abs.createdAt.toDate().toISOString().slice(0,10)) : '-'}</td>
                    <td style={{padding: 8}}><span style={{background: statusColors[abs.status] || '#bbb', color: '#fff', borderRadius: 8, padding: '2px 10px', fontSize: 13}}>{abs.status}</span></td>
                  </tr>
                ))}
                {pagedAbsences.length === 0 && (
                  <tr><td colSpan={7} style={{textAlign: 'center', color: '#888', padding: 16}}>Aucune absence à afficher.</td></tr>
                )}
              </tbody>
            </table>
            {/* Pagination style Material UI */}
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 16, gap: 16}}>
              <span>Éléments par page :</span>
              <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }} style={{padding: '4px 8px', borderRadius: 6}}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span>{totalAbs === 0 ? '0' : `${page * rowsPerPage + 1} – ${Math.min((page + 1) * rowsPerPage, totalAbs)} sur ${totalAbs}`}</span>
              <button onClick={() => setPage(0)} disabled={page === 0} style={{background: 'none', border: 'none', fontSize: 18, cursor: page === 0 ? 'default' : 'pointer', color: '#444'}}>&#124;&lt;</button>
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{background: 'none', border: 'none', fontSize: 18, cursor: page === 0 ? 'default' : 'pointer', color: '#444'}}>&lt;</button>
              <button onClick={() => setPage(p => Math.min(Math.ceil(totalAbs / rowsPerPage) - 1, p + 1))} disabled={page >= Math.ceil(totalAbs / rowsPerPage) - 1} style={{background: 'none', border: 'none', fontSize: 18, cursor: page >= Math.ceil(totalAbs / rowsPerPage) - 1 ? 'default' : 'pointer', color: '#444'}}>&gt;</button>
              <button onClick={() => setPage(Math.max(0, Math.ceil(totalAbs / rowsPerPage) - 1))} disabled={page >= Math.ceil(totalAbs / rowsPerPage) - 1} style={{background: 'none', border: 'none', fontSize: 18, cursor: page >= Math.ceil(totalAbs / rowsPerPage) - 1 ? 'default' : 'pointer', color: '#444'}}>&gt;&#124;</button>
            </div>
          </>
        )}
      </section>
      <section>
        <h2 style={{fontSize: 22, marginBottom: 18}}>Calendrier des Absences</h2>
        <div style={{maxWidth: 900, margin: '0 auto', background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: 24}}>
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16}}>
            <button onClick={() => setCalendarMonth(m => m.subtract(1, 'month'))} style={{background: 'none', border: 'none', fontSize: 22, cursor: 'pointer'}}>&lt;</button>
            <span style={{fontWeight: 600, fontSize: 20}}>{calendarMonth.format('MMMM YYYY')}</span>
            <button onClick={() => setCalendarMonth(m => m.add(1, 'month'))} style={{background: 'none', border: 'none', fontSize: 22, cursor: 'pointer'}}>&gt;</button>
          </div>
          <table style={{width: '100%', borderCollapse: 'collapse', textAlign: 'center'}}>
            <thead>
              <tr style={{background: '#f5f5f5'}}>
                <th>Lun.</th><th>Mar.</th><th>Mer.</th><th>Jeu.</th><th>Ven.</th><th>Sam.</th><th>Dim.</th>
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, wi) => (
                <tr key={wi}>
                  {week.map((d, di) => (
                    <td key={di} style={{verticalAlign: 'top', minWidth: 90, minHeight: 60, border: '1px solid #eee', padding: 4}}>
                      {d && (
                        <div style={{fontWeight: 600, marginBottom: 2}}>{d}</div>
                      )}
                      {d && absencesByDay[d] && absencesByDay[d].map((abs, i) => {
                        const half = isHalfDay(abs, dayjs(calendarMonth).date(d).toDate());
                        return (
                          <div key={abs.id + i}
                            style={{
                              background: half
                                ? '#fff'
                                : abs.status === 'Validé'
                                  ? '#43a04722'
                                  : abs.status === 'Refusé'
                                    ? '#e5393522'
                                    : '#ff980022',
                              borderLeft: `6px solid ${statusColors[abs.status] || '#bbb'}`,
                              borderRadius: 6,
                              marginBottom: 2,
                              padding: '2px 6px',
                              fontSize: 13,
                              cursor: 'pointer',
                              textAlign: 'left',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              position: 'relative',
                              minHeight: 18
                            }}
                            onClick={() => setSelected(abs)}
                          >
                            <span style={{width: 8, height: 8, borderRadius: 8, background: statusColors[abs.status] || '#bbb', display: 'inline-block'}}></span>
                            {abs.employeeName}
                            {half && (
                              <span style={{
                                position: 'absolute',
                                top: 0,
                                bottom: 0,
                                left: half === 'left' ? 0 : '50%',
                                width: '50%',
                                background: half === 'left' ? 'rgba(67,160,71,0.18)' : 'rgba(255,152,0,0.18)',
                                borderTopLeftRadius: half === 'left' ? 6 : 0,
                                borderBottomLeftRadius: half === 'left' ? 6 : 0,
                                borderTopRightRadius: half === 'right' ? 6 : 0,
                                borderBottomRightRadius: half === 'right' ? 6 : 0,
                                zIndex: 1
                              }} />
                            )}
                          </div>
                        );
                      })}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {/* --- Nouvelle section : Liste mensuelle des absences --- */}
      <div style={{marginTop: 48, marginBottom: 32}}>
        <h2>Liste mensuelle des absences</h2>
        <div style={{display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16}}>
          <button onClick={handlePrevListMonth} style={{fontSize: 20, padding: '2px 10px'}}>&lt;</button>
          <span style={{fontWeight: 600, fontSize: 18}}>{listMonth.format('MMMM YYYY')}</span>
          <button onClick={handleNextListMonth} style={{fontSize: 20, padding: '2px 10px'}}>&gt;</button>
        </div>
        <div style={{overflowX: 'auto'}}>
          <table style={{width: '100%', borderCollapse: 'collapse', minWidth: 700}}>
            <thead>
              <tr>
                <th style={{textAlign: 'left', padding: 8}}>Nom</th>
                <th style={{textAlign: 'left', padding: 8}}>Date début</th>
                <th style={{textAlign: 'left', padding: 8}}>Début</th>
                <th style={{textAlign: 'left', padding: 8}}>Date fin</th>
                <th style={{textAlign: 'left', padding: 8}}>Fin</th>
                <th style={{textAlign: 'left', padding: 8}}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {sortedListAbsences.length === 0 && (
                <tr><td colSpan={6} style={{textAlign: 'center', padding: 16}}>Aucune absence sur ce mois</td></tr>
              )}
              {sortedListAbsences.map(abs => (
                <tr key={abs.id} style={{background: getRowColor(abs.status), borderLeft: `6px solid ${getBorderColor(abs.status)}`}}>
                  <td style={{padding: 8}}>{abs.employeeName || abs.employeeEmail}</td>
                  <td style={{padding: 8}}>{formatDateFR(abs.startDate)}</td>
                  <td style={{padding: 8}}>{(abs.startPart || '').toLowerCase()}</td>
                  <td style={{padding: 8}}>{formatDateFR(abs.endDate)}</td>
                  <td style={{padding: 8}}>{(abs.endPart || '').toLowerCase()}</td>
                  <td style={{padding: 8, fontWeight: 600}}>{abs.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminAbsenceManagement; 